import { useForm } from "@tanstack/react-form";

import type { WorkspaceDependency } from "../types";

/** Treat an empty version field as the npm `latest` tag. */
export const DEFAULT_DEPENDENCY_VERSION = "latest";

export interface AddDependencyFormValues {
  name: string;
  version: string;
}

/**
 * The short-lived inline form used to stage a dependency before it is appended
 * to the workspace's persisted dependency list. Domain-free: every domain that
 * bundles user code locks the same `{ name, version }` pairs.
 */
export const useAddDependencyForm = (
  existingNames: string[],
  onSubmit: (value: WorkspaceDependency) => void
) =>
  useForm({
    defaultValues: { name: "", version: "" },
    onSubmit: ({ value }) => {
      if (validateNewDependencyName(value.name, existingNames)) return;
      onSubmit({
        name: value.name.trim(),
        version: value.version.trim() || DEFAULT_DEPENDENCY_VERSION,
      });
    },
  });

/**
 * Validates a candidate new dependency name against the current list. Returns
 * an error message, or `null` when the name may be added — the same non-empty +
 * uniqueness rules the save-path schema enforces, so the inline form gives live
 * feedback before the entry ever reaches the array.
 */
export const validateNewDependencyName = (
  rawName: string,
  existingNames: string[]
): string | null => {
  const name = rawName.trim();
  if (!name) return "Package name is required";
  if (existingNames.includes(name)) return `"${name}" is already listed`;
  return null;
};
